import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  FolderOpen, 
  Search, 
  FileSignature, 
  Scale, 
  ClipboardCheck, 
  Building2,
  Globe,
  X,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';

const services = [
  {
    icon: Globe,
    title: 'Immigration Services',
    description: 'Comprehensive immigration support including visa applications, work permits, green cards, and citizenship petitions for individuals and businesses.',
    hasDetailPage: true,
    detailPageUrl: '/services/immigration',
    fullDescription: 'Our immigration services team provides end-to-end support for all immigration matters. We assist law firms and corporate clients with the complex documentation and compliance requirements of U.S. immigration law.',
    features: [
      'Visa applications (H-1B, L-1, O-1, E-2, etc.)',
      'Green card and permanent residency petitions',
      'Citizenship and naturalization applications',
      'Work permit and EAD processing',
      'Corporate immigration compliance',
      'PERM labor certification support'
    ],
    turnaround: 'Case-dependent',
    idealFor: 'Immigration attorneys and corporations with international workforce needs'
  },
  {
    icon: FileText,
    title: 'Legal Document Preparation',
    description: 'Precise drafting and preparation of pleadings, motions, briefs, and other legal documents with meticulous attention to detail.',
    fullDescription: 'Our legal document preparation services ensure every document meets the highest standards of accuracy and compliance. We work closely with your team to understand case requirements and deliver polished, court-ready documents.',
    features: [
      'Pleadings, motions, and briefs drafting',
      'Legal correspondence and memoranda',
      'Court filings and submissions',
      'Document formatting and citation checking',
      'Proofreading and quality assurance',
      'Rush delivery options available'
    ],
    turnaround: '24-48 hours',
    idealFor: 'Law firms needing reliable document support for litigation matters'
  },
  {
    icon: FolderOpen,
    title: 'Case Management Support',
    description: 'Comprehensive organization and tracking of case files, deadlines, and critical dates to keep your practice running smoothly.',
    fullDescription: 'Stay on top of every case with our comprehensive case management support. We help organize, track, and maintain your case files so nothing falls through the cracks.',
    features: [
      'Case file organization and indexing',
      'Deadline tracking and calendar management',
      'Document management systems setup',
      'Status reporting and updates',
      'Client communication tracking',
      'Evidence and exhibit organization'
    ],
    turnaround: 'Ongoing support',
    idealFor: 'Busy practices managing multiple active cases simultaneously'
  },
  {
    icon: Search,
    title: 'Legal Research',
    description: 'In-depth research using Westlaw, LexisNexis, and other databases to support case strategy and legal arguments.',
    fullDescription: 'Our experienced research team provides thorough legal research to strengthen your case strategy. We utilize premium databases and deliver actionable insights.',
    features: [
      'Case law and statutory research',
      'Regulatory and administrative research',
      'Competitive intelligence gathering',
      'Research memoranda preparation',
      'Citation verification',
      'Legislative history research'
    ],
    turnaround: '24-72 hours',
    idealFor: 'Attorneys requiring comprehensive research support for complex matters'
  },
  {
    icon: FileSignature,
    title: 'Contract Drafting Assistance',
    description: 'Expert support in drafting, reviewing, and organizing contracts, agreements, and corporate documents.',
    fullDescription: 'From simple agreements to complex commercial contracts, our team provides expert drafting and review assistance to protect your clients\' interests.',
    features: [
      'Contract drafting and revisions',
      'Agreement review and redlining',
      'Template creation and maintenance',
      'Contract summary preparation',
      'Due diligence document review',
      'Clause library management'
    ],
    turnaround: '48-72 hours',
    idealFor: 'Corporate law practices and transactional attorneys'
  },
  {
    icon: Scale,
    title: 'Discovery & Litigation Support',
    description: 'Efficient management of discovery processes, document review, and litigation preparation services.',
    fullDescription: 'Navigate complex discovery processes with confidence. Our litigation support team handles everything from document review to deposition preparation.',
    features: [
      'Document review and coding',
      'Discovery request/response preparation',
      'Deposition summaries and digests',
      'Trial preparation assistance',
      'Exhibit preparation and organization',
      'E-discovery support and management'
    ],
    turnaround: 'Project-based',
    idealFor: 'Litigation teams handling document-intensive cases'
  },
  {
    icon: ClipboardCheck,
    title: 'Compliance Documentation',
    description: 'Preparation and maintenance of compliance records, regulatory filings, and audit documentation.',
    fullDescription: 'Keep your clients compliant with our thorough compliance documentation services. We help prepare, organize, and maintain all necessary regulatory records.',
    features: [
      'Regulatory filing preparation',
      'Compliance audit support',
      'Policy and procedure documentation',
      'Record retention management',
      'Compliance calendar tracking',
      'Regulatory correspondence handling'
    ],
    turnaround: '48-96 hours',
    idealFor: 'Firms serving clients in regulated industries'
  },
  {
    icon: Building2,
    title: 'Corporate Filing Services',
    description: 'State and federal filing assistance including incorporations, amendments, annual reports, and UCC filings.',
    fullDescription: 'Streamline your corporate filings with our comprehensive filing services. We handle everything from business formation to ongoing compliance filings.',
    features: [
      'Business entity formation',
      'Annual report preparation and filing',
      'Amendments and name changes',
      'UCC filings and searches',
      'Registered agent services coordination',
      'Good standing certificates'
    ],
    turnaround: '24-48 hours',
    idealFor: 'Business attorneys and corporate legal departments'
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export const ServicesSection = () => {
  const [selectedService, setSelectedService] = useState(null);
  const navigate = useNavigate();

  const openModal = (service) => {
    // If service has a detail page, navigate to it
    if (service.hasDetailPage && service.detailPageUrl) {
      navigate(service.detailPageUrl);
      return;
    }
    setSelectedService(service);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedService(null);
    document.body.style.overflow = 'unset';
  };

  const scrollToContact = () => {
    closeModal();
    setTimeout(() => {
      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <section
        id="services"
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #fdfdfc 0%, rgba(10,25,47,0.04) 50%, rgba(217,48,37,0.06) 100%)'
        }}
        data-testid="services-section"
      >
        {/* Gradient decorations */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#0a192f]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-[#D93025]/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-[#D93025]">
              Our Services
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#0a192f] mt-4 mb-6">
              Comprehensive Legal Support Solutions
            </h2>
            <p className="text-base md:text-lg text-[#64748b] max-w-2xl mx-auto">
              From document preparation to litigation support, we offer a full spectrum of 
              legal support services tailored to your firm's unique needs.
            </p>
          </motion.div>

          {/* Services Bento Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="bento-grid"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className={`service-card bg-white rounded-xl border border-[#e2e2dc]/60 p-8 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 ${
                  index === 0 ? 'lg:col-span-2' : ''
                }`}
                data-testid={`service-card-${index}`}
              >
                <div className="flex flex-col h-full">
                  <div className="w-14 h-14 bg-[#f5f5f0] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0a192f] transition-colors">
                    <service.icon className="h-7 w-7 text-[#0a192f]" />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-[#0a192f] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#64748b] leading-relaxed flex-grow">
                    {service.description}
                  </p>
                  <div className="mt-6 pt-4 border-t border-[#e2e2dc] relative z-10">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openModal(service);
                      }}
                      className="text-sm font-medium text-[#0a192f] hover:text-[#D93025] transition-colors inline-flex items-center gap-2 cursor-pointer"
                      data-testid={`service-learn-more-${index}`}
                    >
                      Learn More
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Service Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
            data-testid="service-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="service-modal"
            >
              {/* Modal Header */}
              <div className="relative px-8 pt-8 pb-6 border-b border-[#e2e2dc] bg-gradient-to-r from-[#f5f5f0] to-white">
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#e2e2dc] transition-colors"
                  data-testid="service-modal-close"
                >
                  <X className="w-5 h-5 text-[#64748b]" />
                </button>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-[#0a192f] rounded-xl flex items-center justify-center flex-shrink-0">
                    <selectedService.icon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-semibold text-[#0a192f] mb-2">
                      {selectedService.title}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#0a192f]/10 rounded-full text-xs font-medium text-[#0a192f]">
                        <span className="w-1.5 h-1.5 bg-[#D93025] rounded-full" />
                        {selectedService.turnaround}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="px-8 py-6 overflow-y-auto max-h-[50vh]">
                <p className="text-[#64748b] leading-relaxed mb-6">
                  {selectedService.fullDescription}
                </p>

                {/* Features List */}
                <div className="mb-6">
                  <h4 className="font-heading font-medium text-[#0a192f] mb-4">What's Included</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {selectedService.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-[#D93025] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#64748b]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Ideal For */}
                <div className="bg-[#f5f5f0] rounded-xl p-4">
                  <h4 className="font-heading font-medium text-[#0a192f] mb-2 text-sm">Ideal For</h4>
                  <p className="text-sm text-[#64748b]">{selectedService.idealFor}</p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-8 py-6 border-t border-[#e2e2dc] bg-[#f5f5f0]/50 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={scrollToContact}
                  className="flex-1 bg-[#0a192f] text-white rounded-xl px-6 py-3 font-medium hover:bg-[#0a192f]/90 transition-all shadow-lg shadow-[#0a192f]/20"
                  data-testid="service-modal-contact"
                >
                  Request This Service
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  onClick={closeModal}
                  variant="outline"
                  className="flex-1 bg-white text-[#0a192f] rounded-xl px-6 py-3 font-medium hover:bg-[#f5f5f0] transition-all border border-[#e2e2dc]"
                  data-testid="service-modal-close-btn"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
